// Direct-to-GCS upload — the path for files over the 32 MiB request-body cap
// (ENG-1820).
//
// Cloud Run / the Google Front End reject request bodies over 32 MiB with a 413
// that carries no CORS headers, so cross-origin the browser can't even read the
// status — `fetch` just rejects with an opaque "Failed to fetch". No server
// change moves that ceiling, because the bytes never arrive. The only way past
// it is to send them somewhere else:
//
//   1. `POST /api/uploads/direct/start` — the API authorizes the upload and
//      returns a short-lived signed URL plus the exact headers its signature
//      commits to.
//   2. Open a GCS resumable session with that URL, then PUT the bytes to it,
//      resuming from the committed offset if the connection drops. That half of
//      the protocol lives in `lib/resumable-upload.ts` — dependency-free, so it
//      never carries our auth headers to storage.googleapis.com.
//   3. `POST /api/uploads/direct/complete` — the API turns the staged object
//      into a Source and returns the same `Upload` shape as a normal upload.
//
// Two browser requirements this depends on, both set by
// `scripts/configure_uploads_bucket.sh`: the app's origin must be allowed for
// POST/PUT on the bucket, and `location` + `range` must be in its CORS
// `responseHeader` list, or steps 2 and 3 can't read what they need.

import type { components } from "@/lib/api-types";

import { API, ApiError, authHeaders, requestJson, type Upload } from "../api";
import {
  attemptUntilDeadline,
  finalizeAuthRetryAvailable,
  shouldRetryFinalize,
  isMissingRoute,
  mayStartFinalizeWork,
  openResumableSession,
  putResumable,
  type UploadProgress as ResumableProgress,
} from "../resumable-upload";

type Schemas = components["schemas"];

export type DirectUploadStart = Schemas["DirectUploadStart"];
export type DirectUploadProgress = ResumableProgress;

/** What a retry of one direct upload needs to know.
 *
 * Caller-owned and mutated in place, because the dangerous failure point is the
 * FINAL step: if `/complete` creates the Source and then loses its response, a
 * retry that started over from `/start` would mint a new token, re-upload the
 * file, and create a **duplicate Source** — the exact failure this ticket
 * exists to remove. `/complete` is idempotent only on the same token, so the
 * token has to outlive the call that obtained it.
 *
 * A failure *before* the bytes are staged needs no such care: no Source was
 * created, and a fresh `/start` is both correct and cheaper than resuming a
 * half-written staging object. */
export type DirectUploadResume = {
  /** Server-minted, set once `/start` succeeds. */
  token?: string;
  /** True once every byte is in GCS, so a retry can go straight to `/complete`. */
  staged?: boolean;
};

/** The API is reachable but this deployment can't stage bytes in GCS (no
 * bucket, or URL signing isn't wired up). Callers fall back to the multipart
 * cap and tell the user the file is too large, rather than surfacing a bare 503
 * for something no retry will fix. */
export class DirectUploadUnavailableError extends Error {
  constructor(detail?: string) {
    super(detail ?? "Large-file upload isn't available here.");
    this.name = "DirectUploadUnavailableError";
  }
}

/**
 * Upload a large file straight to GCS and register it as a Source.
 *
 * Rejects with an `ApiError` for anything the server refused (size, scope,
 * project access), `DirectUploadUnavailableError` when the deployment has no
 * staging bucket, or an `AbortError` `DOMException` on cancel — the same
 * contract as `uploadFileWithProgress`, so callers don't branch on which path
 * ran.
 */
export async function uploadFileDirect(
  file: File,
  opts: {
    tags?: string[];
    projectId?: string;
    scope?: "project_knowledge" | "task_attachment";
    onProgress?: (p: DirectUploadProgress) => void;
    signal?: AbortSignal;
    resume?: DirectUploadResume;
  } = {},
): Promise<Upload> {
  // A previous attempt already staged every byte, so the only step left is the
  // finalize — and repeating it on the same token replays rather than
  // duplicating. Restarting from /start here is what would create a duplicate.
  if (opts.resume?.staged && opts.resume.token) {
    opts.onProgress?.({ loaded: file.size, total: file.size, pct: 100 });
    return finalizeDirectUpload(opts.resume.token, opts.signal);
  }

  let start: DirectUploadStart;
  try {
    start = await requestJson<DirectUploadStart>(`${API}/api/uploads/direct/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        size_bytes: file.size,
        mime_type: file.type || null,
        tags: opts.tags ?? [],
        project_id: opts.projectId ?? null,
        scope: opts.scope ?? "project_knowledge",
      }),
      signal: opts.signal,
    });
  } catch (e) {
    // 501 from /start means specifically "not available here", not "try again"
    // — the route returns it only when there is no bucket or no signing
    // credential, neither of which a retry changes. A 503 from the same route
    // is the opposite (signing failed transiently) and must stay an ordinary
    // retryable error: relabelling it "too large" would be false and would
    // take Retry away from a file that was never over the cap.
    //
    // A 404 can mean the same thing for a different reason: the route isn't
    // there at all. Netlify publishes this app from `main` on every push while
    // the Cloud Run API deploys separately and later, so there is a real window
    // where the new web is live against an API that has never heard of
    // `/direct/*`. Reading THAT as "unavailable here" makes the frontend safe
    // against either revision — a large file gets the honest size message it
    // got before this path existed, instead of a bare 404.
    //
    // But the new handler emits 404s of its own, deliberately: an unknown,
    // deleted, or cross-tenant project answers 404 rather than 403 so the API
    // never confirms what the caller can't see. Those must survive — telling
    // someone their file is "over the upload limit" when the real answer is
    // "that project isn't there" is a false and permanent dead end.
    //
    // The two are distinguishable by body: a routing miss is the framework's
    // own generic `{"detail": "Not Found"}`, while every 404 this API raises
    // carries a specific detail ("Project not found", "upload not found").
    if (e instanceof ApiError && (e.status === 501 || isMissingRoute(e.status, e.detail))) {
      throw new DirectUploadUnavailableError(e.detail);
    }
    throw e;
  }

  if (opts.resume) opts.resume.token = start.upload_token;

  const sessionUri = await openResumableSession(
    start.start_url,
    start.headers as Record<string, string>,
    opts.signal,
  );
  await putResumable(sessionUri, file, {
    onProgress: opts.onProgress,
    signal: opts.signal,
  });
  // Every byte is in GCS. From here a retry must reuse this token — see
  // DirectUploadResume.
  if (opts.resume) opts.resume.staged = true;

  return finalizeDirectUpload(start.upload_token, opts.signal);
}

// Bound on ONE `/complete` attempt. The server-side work is a GCS download plus
// the Files API push, behind a concurrency gate that itself waits up to 120s —
// so this has to be generous.
const FINALIZE_TIMEOUT_MS = 3 * 60_000;
// Bound on the WHOLE finalize, across every attempt. This is the number that
// matters to the user: by this point the modal shows 100%, has hidden Cancel,
// and has disabled every close affordance, so it is how long they can be held
// on "Finishing…" before being handed something they can act on.
//
// One deadline rather than a per-failure-kind attempt budget, because the kinds
// convert into each other. A finalize that outruns the per-attempt timeout is
// still running server-side and still holding its lease, so the next attempt on
// the same token comes back 409 in milliseconds — with separate budgets those
// fast 409s drained the retry allowance and threw while most of the deadline was
// still unspent, which is precisely the case the long wait exists for.
const FINALIZE_DEADLINE_MS = 6 * 60_000;
// Spacing between fast retries (no response, 409, 5xx). Grows so a genuinely
// long finalize is polled cheaply rather than hammered; see `shouldRetryFinalize`
// for why an ambiguous finalize must be retried HERE, on the token still in
// hand, rather than surfaced to a caller that will mint a new one.
const FINALIZE_POLL_BACKOFF_MS = [1_000, 3_000, 6_000, 10_000];
// Worst-case lifetime of ONE server-side finalize worker. FOUR terms, and they
// are sequential in the worst case rather than alternatives: the worker spends
// its whole budget (FINALIZE_BUDGET_S), the multipart trailer takes the tail no
// cooperative check can see (TRANSPORT_TAIL_S), the breach path discards the
// provider file (CLEANUP_TAIL_S — a scalar httpx timeout applies per operation,
// so one DELETE costs connect + write + read), and only then does the claim's
// recovery window open (RECOVERY_GRACE_S).
//
// Every term after the first is time the client is still waiting through, and
// omitting any of them is not conservative — it silently spends the server's
// safety margin, leaving this client to believe a worker still fits when it no
// longer does. Kept in lockstep by a test that checks each term, not the total.
//
// This is what makes a retry expensive. A 409 is a free poll — a worker is
// already running on this token and asking again costs nothing. Every other
// retryable answer means the next attempt would START a worker, and a fresh
// worker awards itself the full budget above with no idea how much of the
// CLIENT's deadline is left. Launch one that can't finish in time and it can
// create a Source after this loop has given up and the token has been dropped —
// the user re-picks, mints a new token, and ends up with two. So a retry that
// starts new work is only allowed while a whole worker still fits.
const SERVER_FINALIZE_WORST_CASE_MS = (210 + 90 + 15 + 15) * 1_000;

/** Turn staged bytes into a Source. Idempotent on `token`, and bounded so a
 * wedged finalize surfaces as a retryable error instead of an endless spinner. */
async function finalizeDirectUpload(token: string, signal?: AbortSignal): Promise<Upload> {
  // Distinguishes OUR per-attempt timeout from a user cancel: both arrive as an
  // AbortError, and only one of them is ours to retry.
  const TIMED_OUT = Symbol("finalize timeout");
  // Opting out of `apiFetch`'s built-in 401 retry (below) would otherwise cost
  // the recovery it provided: a merely EXPIRED token is fatal to this loop
  // without one. So the loop does that recovery itself, where a retry rebuilds
  // the body from a fresh clock. Once only — a 401 that survives a forced
  // refresh is a real authentication failure, and retrying it for the rest of
  // the six minutes just delays telling the user.
  let authRetried = false;
  let needFreshToken = false;

  return attemptUntilDeadline<Upload>(
    async (timeoutMs, _remainingMs, remainingNow) => {
      // The timer starts FIRST, before anything that can stall.
      //
      // `attemptUntilDeadline` checks its clock between attempts; it does not
      // race the attempt promise. So anything awaited here that has no timeout
      // of its own is unbounded no matter what the deadline says — and token
      // acquisition is exactly that: `getIdToken` takes no signal, and a forced
      // refresh talks to the network. Left above this line it could hang the
      // upload forever on "Finishing…", with the six-minute deadline never
      // firing because the loop was never given control back.
      const controller = new AbortController();
      let timedOut = false;
      const onAbort = () => controller.abort();
      if (signal) {
        if (signal.aborted) throw new DOMException("Upload canceled", "AbortError");
        signal.addEventListener("abort", onAbort, { once: true });
      }
      let expire: () => void = () => {};
      const expired = new Promise<never>((_, reject) => {
        expire = () => reject(new Error("finalize attempt timed out"));
      });
      // The race below leaves this rejection unobserved once the warm wins;
      // claim it so it can't surface as an unhandled rejection.
      void expired.catch(() => {});
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
        expire();
      }, timeoutMs);
      try {
        // Acquire the real headers ONCE, here, and hand them to the dispatch.
        //
        // Two earlier attempts at this only half-worked, and the reason is the
        // same both times: `apiFetch` does its own `authStateReady()` /
        // `getIdToken()` immediately before `fetch`, so any lookup left there
        // sits between the verdict below and the wire. Warming first made that
        // lookup fast but not absent; racing the dispatch bounded it but did
        // not stop it spending the margin the verdict was computed against.
        // Reusing these credentials is what actually removes it — after this
        // line nothing on the way to `fetch` touches the network, so the clock
        // `pollOnly` reads is the clock the request goes out on.
        const url = `${API}/api/uploads/direct/complete`;
        const prepared = await Promise.race([
          authHeaders(
            url,
            { method: "POST", headers: { "Content-Type": "application/json" } },
            needFreshToken,
          ),
          expired,
        ]);
        needFreshToken = false;
        // Decided HERE, per attempt, on a clock read after the acquisition
        // above rather than on the snapshot this attempt was handed. `pollOnly`
        // used to start false and only change after a request failed, so the
        // FIRST request always claimed it could start work no matter how much
        // of the six minutes had already gone (a resumed tab, a slow token
        // refresh). Whether a worker still fits is a fact about the clock, not
        // about the previous response.
        const pollOnly = !mayStartFinalizeWork(remainingNow(), SERVER_FINALIZE_WORST_CASE_MS);
        // Still raced, because the abort controller reaches only `fetch` and
        // the loop bounds nothing inside an attempt. With `prepared` supplying
        // the credentials there is no auth lookup left in here to stall, but
        // the race is what makes that a bound rather than a hope.
        const dispatch = requestJson<Upload>(
          url,
          {
            method: "POST",
            body: JSON.stringify({ upload_token: token, poll_only: pollOnly }),
            signal: controller.signal,
          },
          // `pollOnly` above is a verdict about the clock, baked into this
          // body. The built-in 401 retry would force a token refresh and
          // re-send this SAME body — the stale verdict — after a round trip
          // that can cross the worker-fit threshold. Let the 401 surface
          // instead: this loop's next attempt rebuilds the body from a fresh
          // reading, which is the whole point of deciding per attempt.
          { retryOn401: false, preparedHeaders: prepared },
        );
        return await Promise.race([dispatch, expired]);
      } catch (e) {
        if (timedOut && !signal?.aborted) {
          throw Object.assign(new Error("finalize attempt timed out"), { [TIMED_OUT]: true });
        }
        throw e;
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener("abort", onAbort);
      }
    },
    {
      deadlineMs: FINALIZE_DEADLINE_MS,
      attemptTimeoutMs: FINALIZE_TIMEOUT_MS,
      backoffMs: FINALIZE_POLL_BACKOFF_MS,
      classify: (e) => {
        // A user cancel is never ours to retry.
        if (signal?.aborted) return "fatal";
        // Retrying is safe at any point in the deadline because the attempt
        // itself decides whether it may start work — see `pollOnly` above.
        //
        // Our timeout: the server is still working and still holding its lease,
        // so go straight back at it on the same token.
        if (e && typeof e === "object" && TIMED_OUT in e) return "immediate";
        // See `authRetried`: replace the built-in refresh-and-resend with a
        // refresh-and-REDECIDE, so the next body carries a current verdict.
        if (finalizeAuthRetryAvailable(e instanceof ApiError ? e.status : null, authRetried)) {
          authRetried = true;
          needFreshToken = true;
          return "immediate";
        }
        return shouldRetryFinalize(e instanceof ApiError ? e.status : null) ? "backoff" : "fatal";
      },
      onDeadlineExceeded: () =>
        new Error("Still finishing — this may have completed. Check your Sources, or retry."),
    },
  );
}
